import { EMPLOYEE_CONTRACT_ASSIGMENT_ALIAS, MINSK_OFFICE_ID } from 'domain/shared/employee';
import { CreateEditFormDrawer } from 'framework/ViewTypes/contentLayout';
import { POSITION_SLOT_WORKER_TYPE } from '../constants';
import type { PositionSlotFormValues, PreviewPositionSlotListItem } from '../types';
import usePositionSlotsService from '../usePositionSlotsService';
import { getPositionSlotFormSections } from './positionSlotFormSections';
import { getPositionSlotValidationSchema, validateSecondaryCompetencyPairs } from './positionSlotValidation';

type PositionSlotFormDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  positionSlot?: PreviewPositionSlotListItem;
  onSuccess?: () => void | Promise<void>;
};

const createInitialValues = (): PositionSlotFormValues => ({
  specializationId: null,
  technologyId: null,
  competencyLevel: '',
  secondaryCompetencies: [],
  businessFunctionId: '',
  workerType: POSITION_SLOT_WORKER_TYPE.STAFF,
  workRateType: EMPLOYEE_CONTRACT_ASSIGMENT_ALIAS.FULL_TIME,
  positionProfileId: null,
  officeIds: [MINSK_OFFICE_ID],
});

const buildEditInitialValues = (positionSlot: PreviewPositionSlotListItem): PositionSlotFormValues => ({
  specializationId: positionSlot.specializationId,
  technologyId: positionSlot.technologyId,
  competencyLevel: (positionSlot.competencyLevel ?? '') as PositionSlotFormValues['competencyLevel'],
  secondaryCompetencies: (positionSlot.secondaryCompetencies ?? []).map(competency => ({ ...competency })),
  officeIds: positionSlot.officeIds,
  workerType: positionSlot.workerType,
  workRateType: positionSlot.workRateType,
  businessFunctionId: positionSlot.businessFunctionId,
  positionProfileId: positionSlot.positionProfileId ?? null,
});

const PositionSlotFormDrawer = ({ isOpen, onClose, positionSlot, onSuccess }: PositionSlotFormDrawerProps) => {
  const { createPositionSlot, updatePositionSlot } = usePositionSlotsService();
  const isEdit = Boolean(positionSlot);
  const initiallyHasWorker = Boolean(positionSlot?.employeeAlias || positionSlot?.employeeAliases?.length);
  const hasPositionFillRequest = Boolean(positionSlot?.positionFulfillmentRequestId);

  return (
    <CreateEditFormDrawer
      key={`${positionSlot?.id ?? 'create'}-${isOpen}`}
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Edit position' : 'Create position'}
      width={850}
      initialData={positionSlot ? buildEditInitialValues(positionSlot) : createInitialValues()}
      isCreateRequest={!isEdit}
      submitFormFunc={data => {
        const values = data as PositionSlotFormValues;
        const pairsError = validateSecondaryCompetencyPairs(values);
        if (pairsError) {
          return Promise.reject(pairsError);
        }
        return positionSlot ? updatePositionSlot(positionSlot.id, values) : createPositionSlot(values);
      }}
      getValidationSchemaFunc={getPositionSlotValidationSchema}
      handleDirty={false}
      afterSubmit={{
        execute: async () => {
          await onSuccess?.();
          onClose();
        },
        message: isEdit ? 'Position has been successfully updated.' : 'Position has been successfully created.',
      }}
      getSections={data => {
        const values = data as PositionSlotFormValues;
        const definitionDisabled = hasPositionFillRequest || initiallyHasWorker;

        return getPositionSlotFormSections({
          specializationId: values.specializationId,
          technologyId: values.technologyId,
          competencyLevel: values.competencyLevel,
          definitionDisabled,
          locationDisabled: definitionDisabled && !(initiallyHasWorker && positionSlot?.requiredReplacement),
        });
      }}
    />
  );
};

export default PositionSlotFormDrawer;
